import { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useBrandContextStore, useSelectedBrandId } from '@/lib/branding/brandContextStore';
import {
  BarChart3,
  CalendarDays,
  User,
  Settings as SettingsIcon,
  Shield,
  ChevronLeft,
  ChevronRight,
  Briefcase,
  Languages,
  LogOut,
  ChevronUp,
  ChevronDown,
  Gift,
  Boxes,
  Palette,
  Package,
  FolderOpen,
  Calculator,
} from 'lucide-react';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { cn } from '@/lib/cn';
import { useAuth } from '@/lib/auth/useAuth';
import { authApi } from '@/features/auth/api';
import { useFeatureAccess } from '@onxite/plan-sdk';
import { useT } from '@/lib/i18n/useT';
import { LANGUAGES, usePrefsStore } from '@/lib/prefs/usePrefsStore';
import { useIsMobile } from '@/lib/hooks/useBreakpoint';
import { SidebarFavorites } from '@/features/favorites/SidebarFavorites';
import { SidebarBrandTree } from './SidebarBrandTree';

/**
 * Canonical IA per PRD Xotski v2.0 §7.1 (Entity Home):
 *   - Entity Home is reached ONLY by clicking the Xotski logo (top-left).
 *     There is NO 'Home' nav item — brands/entity are the organizing
 *     principle, not a destination.
 *   - My xotskies   — merch, scoped to current project
 *   - Projects      — hierarchical organizer
 *   - Insights      — performance + drill-down
 *   - Settings      — user + workspace
 *   - Admin         — bottom, role=admin only
 */

interface SidebarStore {
  /** Desktop (md+) expanded vs. collapsed. Persisted. */
  open: boolean;
  /** Mobile drawer visibility. Not persisted — resets on reload. */
  mobileOpen: boolean;
  toggle: () => void;
  openMobile: () => void;
  closeMobile: () => void;
  toggleMobile: () => void;
}
export const useSidebar = create<SidebarStore>()(
  persist(
    (set) => ({
      open: true,
      mobileOpen: false,
      toggle: () => set((s) => ({ open: !s.open })),
      openMobile: () => set({ mobileOpen: true }),
      closeMobile: () => set({ mobileOpen: false }),
      toggleMobile: () => set((s) => ({ mobileOpen: !s.mobileOpen })),
    }),
    {
      name: 'xotski.sidebar',
      partialize: (s) => ({ open: s.open }),
    },
  ),
);

interface NavItem {
  id: string;
  label: string;
  icon: React.ReactNode;
  to: string;
  match?: (path: string) => boolean;
  adminOnly?: boolean;
  /** Hide unless this predicate returns true (evaluated against permissions). */
  visible?: boolean;
  /** Pin to the bottom (below the spacer). */
  pinBottom?: boolean;
}

export function Sidebar() {
  const { open, toggle, mobileOpen, closeMobile } = useSidebar();
  const { user, role } = useAuth();
  const location = useLocation();
  const isMobile = useIsMobile();
  // On mobile the drawer is always shown at full width when visible, so labels
  // must always render — `open` only controls the desktop collapsed state.
  const expanded = isMobile ? true : open;

  // Auto-close mobile drawer on navigation so the user lands on the new page
  // with full viewport real estate.
  useEffect(() => {
    closeMobile();
  }, [location.pathname, closeMobile]);

  // Close mobile drawer on Escape (mirrors common drawer UX).
  useEffect(() => {
    if (!mobileOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeMobile();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [mobileOpen, closeMobile]);
  const isAdmin = role === 'admin' || role === 'master';
  // Caso 08 — embeddable survey builder gate. v0 defaults to true on every
  // plan, so the sidebar entry is visible unless the column is flipped off.
  // PRD §9 — Brand Inventory is opt-in per brand AND gated by plan flag.
  // The sidebar item shows when the plan has the flag; the controller
  // still enforces brand-level opt-in (assertBrandOptedIn).
  const planBrandInventory = useFeatureAccess('brandInventory').enabled;
  // Brand Studio is scoped to the brand currently selected in the workspace
  // switcher; the entry deep-links to that brand's studio and hides when no
  // brand is selected (admin/master before picking one).
  const selectedBrandId = useSelectedBrandId();
  const t = useT();

  const NAV: NavItem[] = [
    {
      id: 'brand-studio',
      label: t('sidebar.brandStudio'),
      icon: <Palette className="h-[18px] w-[18px]" />,
      to: selectedBrandId ? `/brand-studio/${selectedBrandId}` : '/brand-studio/new',
      match: (p) => p.startsWith('/brand-studio'),
      /*
       * Oculto por ahora.
       *
       * Se esconde la ENTRADA del menú, no la pantalla: la ruta sigue montada,
       * así que un enlace guardado o un marcador que apunte a `/brand-studio`
       * siguen funcionando. Borrar el elemento habría sido lo mismo de escribir
       * y habría perdido la condición de arriba —sólo con marca elegida—, que
       * hay que recordar el día que vuelva.
       *
       * Para devolverlo: `visible: selectedBrandId != null`.
       */
      visible: false,
    },
    {
      // Brand-scoped projects list. Standalone page that renders ONLY the
      // projects grid (+ create dialog) for the selected brand — no brand
      // hero/KPIs/sibling tabs.
      id: 'brand-projects',
      label: t('sidebar.projects'),
      icon: <FolderOpen className="h-[18px] w-[18px]" />,
      to: selectedBrandId ? `/brand/${selectedBrandId}/projects` : '/brand',
      match: (p) => p.startsWith('/brand/') && p.endsWith('/projects'),
      visible: selectedBrandId != null,
    },
    {
      // Brand-scoped xotski catalog. "My Xotskies" used to be a top-level
      // item; each brand now owns its catalog. This links to a standalone
      // page that renders ONLY the Xotskies catalog (no brand hero/KPIs/tabs).
      id: 'brand-xotskies',
      label: t('sidebar.myXotskies'),
      icon: <Package className="h-[18px] w-[18px]" />,
      to: selectedBrandId ? `/brand/${selectedBrandId}/xotskies` : '/brand',
      match: (p) => p.startsWith('/brand/') && p.endsWith('/xotskies'),
      visible: selectedBrandId != null,
    },
    // "My Xotskies" used to live here at the top-level. Each brand now
    // owns its own xotski catalog inside the brand profile (Xotskies
    // tab), so the sidebar entry is hidden to avoid duplicating it.
    // The route at /stock is still wired in routes.tsx in case other
    // links rely on it.
    {
      id: 'brand-inventory',
      label: t('sidebar.brandInventory'),
      icon: <Boxes className="h-[18px] w-[18px]" />,
      to: '/brand-inventory',
      match: (p) => p.startsWith('/brand-inventory'),
      visible: planBrandInventory,
    },
    {
      // Merch planner (antes la calculadora, CH-09): de los invitados al pedido por premio.
      id: 'merch-planner',
      label: t('sidebar.merchPlanner'),
      icon: <Calculator className="h-[18px] w-[18px]" />,
      to: '/merch-planner',
      match: (p) => p.startsWith('/merch-planner') || p.startsWith('/calculator'),
    },
    {
      // El calendario va junto a Proyectos y no abajo del todo: la pregunta
      // «¿qué hay este mes?» se hace tan a menudo como «¿qué proyectos tengo?».
      id: 'calendar',
      label: t('sidebar.calendar'),
      icon: <CalendarDays className="h-[18px] w-[18px]" />,
      to: '/calendar',
      match: (p) => p.startsWith('/calendar'),
      visible: selectedBrandId != null,
    },
    {
      id: 'insights',
      label: t('sidebar.insights'),
      icon: <BarChart3 className="h-[18px] w-[18px]" />,
      to: '/insights',
    },
    // Contacts and Redeem used to live here at the top level. Contacts has
    // been removed entirely (lives elsewhere now); Redeem moved into the
    // user dropdown menu next to Sign out.
    // The standalone journey-builder destination was removed: building the
    // guest journey happens inside Design Journey, on the project/event, not
    // as its own top-level place. The /surveys/builder route still exists so
    // in-context "edit journey" links keep working.
    // Profile is no longer a top-level nav item — it stays reachable from the
    // user-avatar dropdown (ProfileMenu) at the very bottom of the sidebar.
    {
      id: 'admin',
      label: t('sidebar.admin'),
      icon: <Shield className="h-[18px] w-[18px]" />,
      to: '/admin',
      adminOnly: true,
      pinBottom: true,
    },
    {
      // Single workspace entry — every sub-area (access manager, notifications,
      // billing, followup, integrations, active log, account) lives inside the
      // Settings landing page. Pinned to the bottom so it sits directly above
      // the user avatar (and below Admin when that's visible).
      id: 'settings',
      label: t('sidebar.settings'),
      icon: <SettingsIcon className="h-[18px] w-[18px]" />,
      to: '/settings',
      match: (p) =>
        p === '/settings' ||
        p.startsWith('/settings/') ||
        p.startsWith('/notifications') ||
        p.startsWith('/active-log'),
      pinBottom: true,
    },
  ];

  const items = NAV.filter((i) => {
    if (i.adminOnly && !isAdmin) return false;
    if (i.visible === false) return false;
    return true;
  });
  const lastIsAdmin = items[items.length - 1]?.adminOnly;

  return (
    <aside
      // `data-chrome` re-points the content tokens at the chrome palette for
      // the whole subtree, and the explicit text colour stops descendants that
      // set no colour of their own from inheriting the canvas ink (dark text
      // on the dark frame — invisible).
      data-chrome
      className={cn(
        'fixed inset-y-0 left-0 z-50 flex flex-col border-r border-[var(--color-chrome-border)] bg-[var(--color-chrome-bg)] text-[var(--color-chrome-fg)] transition-[width,transform] duration-150',
        // Mobile (<md): off-canvas drawer. Always full width when shown.
        'w-60',
        mobileOpen ? 'translate-x-0' : '-translate-x-full',
        // Desktop (md+): inline fixed sidebar, no transform; width follows collapsed state.
        'md:z-40 md:translate-x-0',
        open ? 'md:w-60' : 'md:w-16',
      )}
    >
      {/* Logo block (clickable → home) */}
      <Link
        to="/"
        className="flex h-14 items-center gap-3 px-4 transition-colors hover:bg-[var(--color-chrome-bg-elevated)]"
      >
        <img src="/xk-icon.svg" alt="Xotski" className="h-8 w-8 flex-none rounded-lg" />
        {expanded && (
          <span className="font-display text-base font-extrabold tracking-tight text-[var(--color-chrome-fg)]">
            xotski
          </span>
        )}
      </Link>

      {/* Nav items */}
      <nav className="flex flex-1 flex-col gap-1 px-3 py-4 scrollbar-thin">
        {/* Brand context — selected brand + projects/events tree. Shown
            only when a brand is selected (non-full users always have one;
            admin/master only see it after picking one in the switcher). */}
        <SidebarBrandTree expanded={expanded} />

        {items
          .filter((i) => !i.pinBottom && !(lastIsAdmin && i.adminOnly))
          .map((item) => (
            <Link
              key={item.id}
              to={item.to}
              data-tour={`nav-${item.id}`}
              title={!expanded ? item.label : undefined}
              className={cn(
                'group flex items-center rounded-lg border border-transparent text-sm font-semibold transition-colors',
                expanded ? 'gap-3 px-3.5 py-2.5' : 'justify-center py-2.5',
                isActive(item, location.pathname)
                  ? 'border-[var(--color-chrome-accent)] bg-[var(--color-chrome-accent-soft)] text-[var(--color-chrome-accent-ink)]'
                  : 'text-[var(--color-chrome-fg)] hover:bg-[var(--color-chrome-bg-elevated)]',
              )}
            >
              <span className="flex-none">{item.icon}</span>
              {expanded && <span className="flex-1">{item.label}</span>}
            </Link>
          ))}

        {/* Favorites — sits between the main nav and the pinned Admin item */}
        <SidebarFavorites collapsed={!expanded} />

        {/* Pinned-bottom items (Admin) */}
        {items
          .filter((i) => i.pinBottom || (lastIsAdmin && i.adminOnly))
          .map((item, idx) => (
            <Link
              key={item.id}
              to={item.to}
              data-tour={`nav-${item.id}`}
              title={!expanded ? item.label : undefined}
              className={cn(
                idx === 0 && 'mt-auto',
                'group flex items-center rounded-lg border border-transparent text-sm font-semibold transition-colors',
                expanded ? 'gap-3 px-3.5 py-2.5' : 'justify-center py-2.5',
                isActive(item, location.pathname)
                  ? 'border-[var(--color-chrome-accent)] bg-[var(--color-chrome-accent-soft)] text-[var(--color-chrome-accent-ink)]'
                  : 'text-[var(--color-chrome-fg)] hover:bg-[var(--color-chrome-bg-elevated)]',
              )}
            >
              <span className="flex-none">{item.icon}</span>
              {expanded && <span className="flex-1">{item.label}</span>}
            </Link>
          ))}
      </nav>

      {/* Profile menu — pinned to bottom, opens UPWARD */}
      <div className="border-t border-[var(--color-chrome-border)] p-2">
        <ProfileMenu collapsed={!expanded} email={user?.email ?? ''} firstname={user?.firstname} />
      </div>

      {/* Copyright footer — only visible when sidebar is expanded */}
      {expanded && (
        <div className="border-t border-[var(--color-chrome-border)] px-4 py-2.5 text-[10px] leading-tight text-[var(--color-chrome-fg-muted)]">
          <div className="font-bold text-[var(--color-chrome-fg)]">Xotski v2</div>
          <div>
            {t('sidebar.copyright.product')}{' '}
            <a
              href="https://www.onxite.com"
              target="_blank"
              rel="noopener noreferrer"
              className="font-bold text-[var(--color-chrome-accent-ink)] hover:underline"
            >
              Onxite
            </a>{' '}
            · © 2026
          </div>
        </div>
      )}

      {/* Collapse toggle on right edge — desktop only */}
      <button
        type="button"
        onClick={toggle}
        aria-label={open ? t('sidebar.collapse') : t('sidebar.expand')}
        className="absolute -right-3 top-1/2 hidden h-6 w-6 -translate-y-1/2 place-items-center rounded-full border border-[var(--color-chrome-border)] bg-[var(--color-chrome-bg)] text-[var(--color-chrome-fg)] shadow hover:text-[var(--color-chrome-accent-ink)] md:grid"
      >
        {open ? <ChevronLeft className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
      </button>
    </aside>
  );
}

function isActive(item: NavItem, pathname: string): boolean {
  if (item.match) return item.match(pathname);
  return pathname === item.to;
}

// ───────────────────────────────────────────────────────────────────────────
// Profile menu (opens upward; matches the XotskiHome story behavior)
// ───────────────────────────────────────────────────────────────────────────

function ProfileMenu({
  collapsed,
  email,
  firstname,
}: {
  collapsed: boolean;
  email: string;
  firstname?: string;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const { logout } = useAuth();
  const t = useT();
  const language = usePrefsStore((s) => s.language);
  const setLanguage = usePrefsStore((s) => s.setLanguage);
  const currentLang = LANGUAGES.find((l) => l.id === language) ?? LANGUAGES[0];
  const nextLang =
    LANGUAGES[(LANGUAGES.findIndex((l) => l.id === language) + 1) % LANGUAGES.length];
  const initials = (firstname?.[0] ?? email[0] ?? '?').toUpperCase();
  const displayName = firstname || email.split('@')[0];

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  return (
    <div ref={ref} className="relative">
      {/* Submenu opens UPWARD */}
      {open && (
        <div className="absolute bottom-[calc(100%+8px)] left-0 right-0 z-50 flex flex-col gap-0.5 rounded-xl border border-[var(--color-chrome-border)] bg-[var(--color-chrome-bg)] p-1.5 shadow-[0_-8px_24px_rgba(0,0,0,0.4)]">
          {/* Profile — also stays in the main sidebar as a top-level link;
              kept here too for quick access from the avatar menu. */}
          <ProfileMenuItem
            icon={<User className="h-4 w-4" />}
            label={t('sidebar.profile')}
            onClick={() => {
              setOpen(false);
              navigate('/profile');
            }}
          />
          {/* Redeem — moved out of the main nav; lives only in this menu. */}
          <ProfileMenuItem
            icon={<Gift className="h-4 w-4" />}
            label={t('sidebar.redeem')}
            onClick={() => {
              setOpen(false);
              navigate('/redeem');
            }}
          />
          {/* Plan / billing — was labeled "Workspace" originally. */}
          <ProfileMenuItem
            icon={<Briefcase className="h-4 w-4" />}
            label={t('sidebar.profileMenu.plan')}
            sublabel={t('sidebar.profileMenu.planSublabel')}
            highlight
            onClick={() => {
              setOpen(false);
              navigate('/settings/billing');
            }}
          />
          <ProfileMenuItem
            icon={<Languages className="h-4 w-4" />}
            label={t('sidebar.profileMenu.language')}
            sublabel={`${currentLang.flag} · ${currentLang.label} → ${nextLang.flag}`}
            onClick={() => {
              setLanguage(nextLang.id);
              setOpen(false);
            }}
          />
          <div className="mx-2 my-1 h-px bg-[var(--color-chrome-border)]" />
          <ProfileMenuItem
            icon={<LogOut className="h-4 w-4" />}
            label={t('common.signOut')}
            onClick={async () => {
              setOpen(false);
              // Clear the server-side single-session record FIRST so the API
              // call goes out with a valid token; only then drop local state.
              await authApi.logout().catch(() => undefined);
              useBrandContextStore.getState().clear();
              logout();
              navigate('/login');
            }}
          />
        </div>
      )}

      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        title={collapsed ? `${displayName} · ${t('sidebar.profileMenu.openMenu')}` : undefined}
        className={cn(
          'flex w-full items-center gap-2.5 rounded-lg border text-left transition-colors',
          collapsed ? 'justify-center py-2.5 px-0' : 'py-2.5 px-3',
          open
            ? 'border-[var(--color-chrome-border)] bg-[var(--color-chrome-bg-elevated)]'
            : 'border-transparent hover:bg-[var(--color-chrome-bg-elevated)]',
        )}
      >
        <span className="grid h-8 w-8 flex-none place-items-center rounded-full bg-[var(--color-chrome-accent)] text-[11px] font-extrabold text-[var(--color-fg-on-accent)]">
          {initials}
        </span>
        {!collapsed && (
          <>
            <span className="min-w-0 flex-1">
              <span className="block truncate text-xs font-bold text-[var(--color-chrome-fg)]">
                {displayName}
              </span>
              <span className="block truncate text-[10px] text-[var(--color-chrome-fg-muted)]">
                {email}
              </span>
            </span>
            {open ? (
              <ChevronDown className="h-3 w-3 flex-none text-[var(--color-chrome-fg-muted)]" />
            ) : (
              <ChevronUp className="h-3 w-3 flex-none text-[var(--color-chrome-fg-muted)]" />
            )}
          </>
        )}
      </button>
    </div>
  );
}

function ProfileMenuItem({
  icon,
  label,
  sublabel,
  highlight,
  onClick,
}: {
  icon: React.ReactNode;
  label: string;
  sublabel?: string;
  highlight?: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left transition-colors',
        highlight
          ? 'bg-[var(--color-chrome-accent-soft)] text-[var(--color-chrome-accent-ink)] hover:bg-[var(--color-chrome-accent-soft)]'
          : 'text-[var(--color-chrome-fg)] hover:bg-[var(--color-chrome-bg-elevated)]',
      )}
    >
      <span
        className={cn(
          'flex h-5 w-5 flex-none items-center justify-center',
          highlight ? 'text-[var(--color-chrome-accent-ink)]' : 'text-[var(--color-chrome-fg-muted)]',
        )}
      >
        {icon}
      </span>
      <span className="flex-1">
        <span className="block text-xs font-bold">{label}</span>
        {sublabel && (
          <span className="block text-[10px] text-[var(--color-chrome-fg-muted)]">{sublabel}</span>
        )}
      </span>
    </button>
  );
}
